/**
 * Moldura de card da home da plataforma, nas três formas do mockup.
 *
 * Puramente apresentacional e sem noção de curso ou módulo: quem sabe o que o
 * card significa é quem o monta (CourseCard para a vitrine, ContinueWatching
 * para os módulos). Existe para que as duas fileiras não repitam scrim, tag,
 * hover e brilho de canto.
 */

import React from 'react';
import { LockIcon, PlayIcon, SpinnerIcon } from './icons';

export type PlatformCardVariant = 'vertical' | 'horizontal' | 'thumb';

export interface PlatformCardProps {
  /** Retrato (vitrine), paisagem ou thumb menor (continue assistindo). */
  variant?: PlatformCardVariant;
  title: string;
  subtitle?: string | null;
  /** Capa. Sem ela, o card usa `gradient`. */
  image?: string;
  /** Classes de gradiente do placeholder, no formato Tailwind `from-* to-*`. */
  gradient: string;
  /** Etiqueta no topo (ex.: NOVO). */
  tag?: string | null;
  tagTone?: 'accent' | 'muted';
  /** Cadeado no canto, com o motivo no title. */
  locked?: boolean;
  lockedTitle?: string;
  /** Spinner cobrindo o card (troca de curso em andamento). */
  busy?: boolean;
  /** Card sem conteúdo disponível: esmaece e não responde ao hover. */
  dimmed?: boolean;
  /** Play central no hover. No mockup só a fileira "continue" tem. */
  showPlayOnHover?: boolean;
  /** Realce de "é este o curso atual". */
  highlighted?: boolean;
  onClick?: () => void;
  disabled?: boolean;
  ariaLabel?: string;
  testId?: string;
}

/**
 * Dimensões do mockup traduzidas para proporção + largura por breakpoint. Altura
 * fixa em px daria um card ocupando metade de uma tela de 360.
 */
const VARIANTS: Record<PlatformCardVariant, string> = {
  vertical: 'w-[150px] sm:w-[186px] aspect-[186/278]',
  horizontal: 'w-[240px] sm:w-[300px] aspect-[300/169]',
  thumb: 'w-[200px] sm:w-[236px] aspect-[236/118]',
};

const PlatformCard: React.FC<PlatformCardProps> = ({
  variant = 'vertical',
  title,
  subtitle,
  image,
  gradient,
  tag,
  tagTone = 'accent',
  locked = false,
  lockedTitle,
  busy = false,
  dimmed = false,
  showPlayOnHover = false,
  highlighted = false,
  onClick,
  disabled = false,
  ariaLabel,
  testId,
}) => {
  const interactive = !dimmed && !busy;

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || busy}
      aria-label={ariaLabel ?? title}
      data-testid={testId}
      className={`group relative block overflow-hidden rounded-xl border text-left
        transition-[transform,box-shadow,border-color] duration-300 ease-mv
        ${VARIANTS[variant]}
        ${
          interactive
            ? 'cursor-pointer border-white/10 hover:-translate-y-1 hover:scale-[1.02] hover:border-mv-accent/65 hover:shadow-[0_18px_40px_-14px_rgba(178,23,195,.55)]'
            : 'cursor-default border-white/10'
        }
        ${highlighted ? 'border-mv-accent ring-2 ring-mv-accent/40' : ''}
        ${busy ? 'cursor-wait opacity-70' : ''}
        focus:outline-none focus-visible:ring-2 focus-visible:ring-mv-accent-hi`}
    >
      <div className="mv-card-sheen absolute inset-0">
        {image ? (
          <img
            src={image}
            alt=""
            loading="lazy"
            decoding="async"
            className={`h-full w-full object-cover ${dimmed ? 'opacity-40 grayscale' : ''}`}
          />
        ) : (
          <div
            className={`h-full w-full bg-gradient-to-br ${gradient} ${dimmed ? 'opacity-30' : ''}`}
          />
        )}
      </div>

      {/* Legibilidade do texto sobre capa ou gradiente. */}
      <div className="absolute inset-0 flex flex-col justify-end bg-gradient-to-t from-[#0A1028]/90 from-[4%] to-transparent to-[55%] p-3.5">
        {tag && (
          // mb-auto empurra o texto para o rodapé sem posicionamento absoluto.
          <span
            className={`mb-auto self-start rounded-md px-2 py-1 text-[10px] font-bold uppercase tracking-[0.14em] ${
              tagTone === 'muted'
                ? 'bg-mv-surface-2/90 text-mv-muted'
                : 'bg-mv-accent/85 text-white'
            }`}
          >
            {tag}
          </span>
        )}

        <p className="line-clamp-2 font-display text-[15px] font-bold leading-tight text-white drop-shadow-md">
          {title}
        </p>
        {subtitle && (
          <p className="mt-0.5 line-clamp-1 text-[11.5px] font-medium text-[#c3cbe6]">
            {subtitle}
          </p>
        )}
      </div>

      {locked && (
        <span
          className="absolute right-3 top-3 rounded-full bg-black/50 p-1.5 text-mv-text/80 backdrop-blur-sm"
          title={lockedTitle}
        >
          <LockIcon className="h-3.5 w-3.5" />
        </span>
      )}

      {busy && (
        <span className="absolute inset-0 flex items-center justify-center bg-black/50">
          <SpinnerIcon className="h-8 w-8 text-mv-accent-hi" />
        </span>
      )}

      {showPlayOnHover && interactive && (
        <span className="absolute inset-0 flex items-center justify-center opacity-0 transition-opacity duration-200 group-hover:opacity-100">
          <span className="flex h-11 w-11 items-center justify-center rounded-full bg-mv-accent/90 shadow-[0_6px_20px_rgba(0,0,0,.4)]">
            <PlayIcon className="ml-0.5 h-[18px] w-[18px] text-white" />
          </span>
        </span>
      )}
    </button>
  );
};

export default PlatformCard;
